import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject
} from 'react';

import { FALLBACK_SNAP_PCT } from './bottom-sheet.constants';
import {
  findNearestSnap,
  findNearestSnapIndex,
  getContentFocusables,
  resolveViewportHeight,
  safeInnerHeight,
  safeReleasePointerCapture,
  safeSetPointerCapture,
  snapPctAt
} from './bottom-sheet.utils';

/** Keep a ref pointing at the latest value of `value` without triggering renders. */
export const useLatestRef = <T>(value: T): RefObject<T> => {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
};

/** Track `window.innerHeight` reactively; returns 0 in SSR. */
export const useViewportHeight = (): number => {
  const [h, setH] = useState<number>(safeInnerHeight);

  useEffect(() => {
    const win = globalThis.window;
    if (win === undefined) return;
    const onResize = () => setH(win.innerHeight);
    win.addEventListener('resize', onResize);
    return () => win.removeEventListener('resize', onResize);
  }, []);

  return h;
};

interface UseFocusTrapOptions {
  open: boolean;
  panelRef: RefObject<HTMLElement | null>;
  onClose: () => void;
}

/**
 * Focus-trap for modal-like surfaces: initial focus on open, Tab/Shift+Tab
 * cycling within the panel, Escape to close, and focus restoration on close.
 * The drag handle is excluded via `getContentFocusables` so chrome doesn't
 * steal initial focus from real content.
 */
export const useFocusTrap = ({ open, panelRef, onClose }: UseFocusTrapOptions): void => {
  const onCloseRef = useLatestRef(onClose);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const activeElement = document.activeElement;
    previouslyFocusedRef.current = activeElement instanceof HTMLElement ? activeElement : null;

    const panel = panelRef.current;
    if (panel) {
      const initial = getContentFocusables(panel)[0];
      (initial ?? panel).focus();
    }

    // Scope the listener to the panel (not document) so two stacked sheets don't both react to Escape:
    // focus is trapped inside the panel, so panel-scoped Escape/Tab is sufficient and avoids cross-component bleed.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current?.();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusable = getContentFocusables(panelRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        panelRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable.at(-1);
      if (!last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    panel?.addEventListener('keydown', handleKeyDown);
    const previouslyFocused = previouslyFocusedRef.current;

    return () => {
      panel?.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [open, panelRef, onCloseRef]);
};

interface UseBottomSheetDragOptions {
  open: boolean;
  viewportH: number;
  snapPoints: readonly number[];
  initialSnap: number;
  maxHeightPct: number;
  minHeightPx: number;
  onClose: () => void;
  onResize?: (pct: number) => void;
  onSnapChange?: (pct: number) => void;
}

interface BottomSheetDragApi {
  heightPx: number;
  isDragging: boolean;
  handlePointerDown: (e: ReactPointerEvent<HTMLDivElement>) => void;
  handlePointerMove: (e: ReactPointerEvent<HTMLDivElement>) => void;
  handlePointerUp: (e: ReactPointerEvent<HTMLDivElement>) => void;
  handleKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => void;
}

/**
 * Owns the panel's height state machine: live pointer drag, keyboard snap
 * stepping, and "settle to nearest snap on release." Invokes `onClose` when
 * the user releases below the lowest snap point (close-on-swipe-down).
 */
export const useBottomSheetDrag = ({
  open,
  viewportH,
  snapPoints,
  initialSnap,
  maxHeightPct,
  minHeightPx,
  onClose,
  onResize,
  onSnapChange
}: UseBottomSheetDragOptions): BottomSheetDragApi => {
  const onCloseRef = useLatestRef(onClose);
  const onResizeRef = useLatestRef(onResize);
  const onSnapChangeRef = useLatestRef(onSnapChange);

  const initialPct = snapPctAt(snapPoints, initialSnap);

  const [heightPx, setHeightPx] = useState<number>(() => initialPct * safeInnerHeight());
  const [isDragging, setIsDragging] = useState(false);

  const heightRef = useRef(0);
  const dragStartYRef = useRef(0);
  const dragStartHeightRef = useRef(0);
  const isDraggingRef = useRef(false);
  const lastReleasedPctRef = useRef<number>(initialPct);

  // Reset to the initial snap on every open/close transition. The component instance
  // persists across close/reopen, so heightPx survives; resetting on CLOSE (while the
  // panel is unmounted and the reset is invisible) is what guarantees a reopen's first
  // render starts at the initial snap, instead of committing the previous expanded
  // height and animating back down.
  useLayoutEffect(() => {
    if (viewportH <= 0) return;
    const next = initialPct * viewportH;
    setHeightPx(next);
    heightRef.current = next;
    lastReleasedPctRef.current = initialPct;
  }, [open, viewportH, initialPct]);

  // Mirror heightPx into a ref so pointer handlers read the latest without re-binding.
  useEffect(() => {
    heightRef.current = heightPx;
  }, [heightPx]);

  const commitSnap = useCallback(
    (pct: number, vh: number) => {
      const nextPx = pct * vh;
      heightRef.current = nextPx;
      setHeightPx(nextPx);
      if (pct !== lastReleasedPctRef.current) {
        lastReleasedPctRef.current = pct;
        onSnapChangeRef.current?.(pct);
      }
    },
    [onSnapChangeRef]
  );

  const settleAtSnap = useCallback(
    (currentHeightPx: number, vh: number) => {
      if (vh <= 0) return;
      const currentPct = currentHeightPx / vh;
      const lowestSnap = snapPoints[0] ?? FALLBACK_SNAP_PCT;
      const highestSnap = snapPoints.at(-1) ?? FALLBACK_SNAP_PCT;

      // Released below the lowest snap → close (swipe-down-to-close gesture).
      if (currentPct < lowestSnap) {
        onCloseRef.current?.();
        return;
      }

      // Released above the highest snap → clamp.
      if (currentPct > highestSnap) {
        commitSnap(highestSnap, vh);
        return;
      }

      commitSnap(findNearestSnap(snapPoints, currentPct), vh);
    },
    [snapPoints, onCloseRef, commitSnap]
  );

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      safeSetPointerCapture(e.currentTarget, e.pointerId);
      dragStartYRef.current = e.clientY;
      dragStartHeightRef.current = heightRef.current || heightPx;
      isDraggingRef.current = true;
      setIsDragging(true);
    },
    [heightPx]
  );

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) return;
      const vh = resolveViewportHeight(viewportH);
      if (vh <= 0) return;
      const delta = dragStartYRef.current - e.clientY; // up = positive
      const maxPx = maxHeightPct * vh;
      const next = Math.max(minHeightPx, Math.min(dragStartHeightRef.current + delta, maxPx));
      heightRef.current = next;
      setHeightPx(next);
      onResizeRef.current?.(next / vh);
    },
    [viewportH, maxHeightPct, minHeightPx, onResizeRef]
  );

  const handlePointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) return;
      safeReleasePointerCapture(e.currentTarget, e.pointerId);
      isDraggingRef.current = false;
      setIsDragging(false);

      const vh = resolveViewportHeight(viewportH);
      settleAtSnap(heightRef.current || heightPx, vh);
    },
    [viewportH, heightPx, settleAtSnap]
  );

  /** Move one snap step in `direction`; closing past the lowest snap. */
  const stepSnap = useCallback(
    (direction: -1 | 1) => {
      const vh = resolveViewportHeight(viewportH);
      if (vh <= 0) return;
      const currentPct = heightRef.current / vh || initialPct;

      let idx = snapPoints.findIndex(sp => Math.abs(sp - currentPct) < 0.02);
      if (idx === -1) idx = findNearestSnapIndex(snapPoints, currentPct);

      const nextIdx = idx + direction;
      if (nextIdx < 0) {
        onCloseRef.current?.();
        return;
      }
      if (nextIdx >= snapPoints.length) return;

      commitSnap(snapPoints[nextIdx] ?? initialPct, vh);
    },
    [viewportH, snapPoints, initialPct, onCloseRef, commitSnap]
  );

  const jumpToEdge = useCallback(
    (edge: 'first' | 'last') => {
      const vh = resolveViewportHeight(viewportH);
      if (vh <= 0) return;
      const pct = edge === 'last' ? snapPoints.at(-1) ?? FALLBACK_SNAP_PCT : snapPoints[0] ?? FALLBACK_SNAP_PCT;
      // Delegate to commitSnap so Home/End share the same dedup + lastReleasedPctRef bookkeeping as drag-release.
      commitSnap(pct, vh);
    },
    [viewportH, snapPoints, commitSnap]
  );

  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          stepSnap(1);
          return;
        case 'ArrowDown':
          e.preventDefault();
          stepSnap(-1);
          return;
        case 'Home':
          e.preventDefault();
          jumpToEdge('last');
          return;
        case 'End':
          e.preventDefault();
          jumpToEdge('first');
      }
    },
    [stepSnap, jumpToEdge]
  );

  return { heightPx, isDragging, handlePointerDown, handlePointerMove, handlePointerUp, handleKeyDown };
};
